import moment from 'moment';
import { PlatformChartExportItemDto } from '@data/_generated-api/platform-api/models/platform-chart-export-item-dto';

/**
 * Exportformaten van de chart manager (docs/human-design/hd-chart-export.md). Het eigen formaat
 * is terug te importeren; de andere bootsen het exportformaat van die software na, zodat hun
 * eigen importfunctie het bestand leest.
 * - `mybodygraph-mmi`: MyBodyGraph importeert enkel ".mmi" (Maia Mechanics Desktop); het
 *   bestand volgt de v2-opbouw, de breedst gelezen variant.
 */
export type ChartExportFormat =
  | 'mechanics-in-motion'
  | 'maia-online-csv'
  | 'maia-desktop-mmi-v2'
  | 'maia-desktop-mmi-v3'
  | 'mybodygraph-mmi';

export const CHART_EXPORT_FORMATS: readonly ChartExportFormat[] = [
  'mechanics-in-motion',
  'maia-online-csv',
  'maia-desktop-mmi-v2',
  'maia-desktop-mmi-v3',
  'mybodygraph-mmi',
];

/** Geboortegegevens van één standaard-chart, genormaliseerd voor alle schrijvers. */
export interface ChartExportRecord {
  readonly name: string;
  /** Geboortemoment (UTC); bepaalt de chart. */
  readonly birthUtc: moment.Moment;
  /**
   * Kloktijd op de geboorteplaats als UTC-moment zonder offset (wandklok). Ontbreekt wanneer er
   * geen echte lokale tijd is: een chart zonder locatie of met onbekende tijdzone (dan staat de
   * tijd in UTC, en een schrijver mag die nooit als kloktijd voorstellen).
   */
  readonly birthLocal?: moment.Moment;
  /** IANA-tijdzone ("Europe/Brussels"); ontbreekt samen met `birthLocal`. */
  readonly timezone?: string;
  readonly city?: string;
  /** ISO-2 ("BE"), wanneer bekend. */
  readonly countryCode?: string;
  /** Engelse landnaam ("Belgium"): uit de code afgeleid, of de landtekst van een ouder document. */
  readonly countryName?: string;
  readonly latitude?: number;
  readonly longitude?: number;
}

const UTC_TIMEZONE_IDS: ReadonlySet<string> = new Set(['utc', 'etc/utc', 'gmt', 'etc/gmt', 'z']);

/** API-rij → record; `undefined` wanneer naam of geboortemoment ontbreekt (niets om te exporteren). */
export function toChartExportRecord(dto: PlatformChartExportItemDto): ChartExportRecord | undefined {
  const name = dto.name?.trim();
  const birthUtc = dto.birthDateUtc ? moment.utc(dto.birthDateUtc, moment.ISO_8601) : undefined;
  if (!name || !birthUtc?.isValid()) return undefined;

  const timezone = dto.timezone?.trim();
  const hasRealTimezone = !!timezone && !UTC_TIMEZONE_IDS.has(timezone.toLowerCase());
  // De kolom is een kloktijd zonder zone: als UTC-moment parsen houdt de wandklok exact.
  const birthLocal = hasRealTimezone && dto.birthDateLocal ? moment.utc(dto.birthDateLocal, moment.ISO_8601) : undefined;
  const hasLocal = !!birthLocal?.isValid();

  const country = dto.birthCountry?.trim() || undefined;
  const countryCode = country && /^[A-Za-z]{2}$/.test(country) ? country.toUpperCase() : undefined;

  return {
    name,
    birthUtc,
    birthLocal: hasLocal ? birthLocal : undefined,
    timezone: hasLocal ? timezone : undefined,
    city: dto.birthCity?.trim() || undefined,
    countryCode,
    countryName: countryCode ? englishCountryName(countryCode) : country,
    latitude: dto.latitude ?? undefined,
    longitude: dto.longitude ?? undefined,
  };
}

/** Offset (minuten, lokaal − UTC) zoals de chart hem toont; 0 zonder echte lokale tijd. */
export function recordUtcOffsetMinutes(record: ChartExportRecord): number {
  return record.birthLocal ? Math.round(record.birthLocal.diff(record.birthUtc, 'seconds') / 60) : 0;
}

/** Wandklok voor schrijvers die altijd een lokale tijd vragen: zonder echte lokale tijd is dat UTC. */
export function recordWallClock(record: ChartExportRecord): moment.Moment {
  return (record.birthLocal ?? record.birthUtc).clone();
}

/** Minuten → "GMT+1", "GMT-3:30"; 0 wordt kaal "GMT" (zoals Maia Online). */
export function formatGmtOffsetLabel(offsetMinutes: number): string {
  if (offsetMinutes === 0) return 'GMT';
  const sign = offsetMinutes < 0 ? '-' : '+';
  const absoluteMinutes = Math.abs(offsetMinutes);
  const hours = Math.floor(absoluteMinutes / 60);
  const minutes = absoluteMinutes % 60;
  return minutes === 0 ? `GMT${sign}${hours}` : `GMT${sign}${hours}:${String(minutes).padStart(2, '0')}`;
}

let regionNames: Intl.DisplayNames | undefined;

function englishCountryName(countryCode: string): string | undefined {
  try {
    regionNames ??= new Intl.DisplayNames(['en'], { type: 'region' });
    const name = regionNames.of(countryCode);
    return name && name !== countryCode ? name : undefined;
  } catch {
    return undefined;
  }
}
